import React, { useState, useEffect } from "react";
import { useTheme } from '@repo/shared';
import { useParams, useHistory } from "react-router-dom";
import DatePicker from "react-multi-date-picker"
import DatePanel from "react-multi-date-picker/plugins/date_panel"
import moment from 'moment';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'

import { confirmAlert } from 'react-confirm-alert'; // Import
import 'react-confirm-alert/src/react-confirm-alert.css'; // Import css

import "react-datepicker/dist/react-datepicker.css";
import { Dropdown, DropdownButton, Spinner, Container, Card, Modal, Button } from 'react-bootstrap';
import LoadingSpinner from '../common/LoadingSpinner';
import ConfigDataService from "../../services/config/ConfigService";
import UserDataService from "../../services/user/UserService";
import StudentDataService from "../../services/student/StudentService";
import SchoolDataService from "../../services/school/SchoolService";
import ClassDataService from "../../services/class/ClassService";

import { useAcademicYear } from "../../contexts/AcademicYearContext";

const Class = props => {
  const { id } = useParams();
  const history = useHistory();
  const initialClassState = ClassDataService.initialiseNewClass();
  const [currentClass, setCurrentClass] = useState(initialClassState);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [updateMode, setUpdateMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState();
  const [selectedSemesterAndYear, setSelectedSemesterAndYear] = useState({});
  const [allSemesterDates, setAllSemesterDates] = useState([]);
  const [semesterDates, setSemesterDates] = useState();
  const [vacationDates, setVacationDates] = useState([]);
  const [studentsForClass, setStudentsForClass] = useState([]);
  // Snapshot of the class membership when the current editing context was loaded.
  const [originalStudentIds, setOriginalStudentIds] = useState(new Set());
  const [studentsForSchool, setStudentsForSchool] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState("");

  const daysOfWeek = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
  const semesters = ['Summer', 'Winter'];

  const { activeYear, activeSemester } = useAcademicYear();
  
  const { appTheme } = useTheme();
  // STYLING: Modern style objects for a cleaner look
  const studentListStyles = {
    listStyle: 'none',
    padding: '0',
    margin: '0',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    height: '350px',
    minWidth: 0,
    overflowY: 'auto',
    backgroundColor: '#f8f9fa'
  };

  const studentItemStyles = {
    padding: '0.75rem 1.25rem',
    borderBottom: '1px solid #dee2e6',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease-in-out',
    position: 'relative',
  };

  const getClass = id => {
    ClassDataService.get(id)
      .then(response => {
        console.log('got this class', response);
        //convert the dates from strings to Date
        response.dates = response.dates.map(d => new Date(Date.parse(d)));

        setCurrentClass({ ...response });
        setSelectedSemesterAndYear({ semester: response.semester, schoolYear: response.schoolYear });
        setSelectedSchoolId(response.school.id);
        setLoading(false);
      })
      .catch(e => {
        console.log(e);
      });
  };

  const addStudentToClass = student => {
    setStudentsForClass(previous => [...previous, student]);
  }

  const removeStudentFromClass = student => {
    setStudentsForClass(previous => previous.filter(s => s.id !== student.id));
  }

  // Highlight only students whose current membership differs from the original
  // membership, so moving a student back clears the highlight automatically.
  const hasStudentMembershipChanged = student => {
    const wasInClass = originalStudentIds.has(student.id);
    const isInClass = studentsForClass.some(s => s.id === student.id);
    return wasInClass !== isInClass;
  }

  const getStudentItemStyles = student => ({
    ...studentItemStyles,
    ...(hasStudentMembershipChanged(student) ? {
      backgroundColor: '#eef8f0',
      borderLeft: '3px solid #8bc69a',
    } : {}),
  });

  const getStudentsForSchool = (schoolId, schoolYear, semester) => {
    if (schoolId && schoolYear) {
      setErrorMessage('');
      StudentDataService.getAllStudentsForSchoolAndYear(schoolId, schoolYear)
        .then(response => {
          //Filter out students who are already assigned to a class other than this one for this year and semester
          const studentsWithoutClass = response.filter(s => {
            const existingClass = s.classes.find(c => {
              return c.classId !== id && c.schoolYear === schoolYear && c.semester === semester;
            })
            return !existingClass;
          })
          setStudentsForSchool(studentsWithoutClass);
        })
        .catch(e => {
          console.log(e);
        });
    } else {
      if (!schoolYear && !schoolId) {
        //Don't show anything yet
      } else {
        if (!schoolYear) {
          setErrorMessage('Please select the school year to see Students available for this class');
        } else if (!schoolId) {
          setErrorMessage('Please select the school to see Students available for this class');
        }
      }
    }
  };

  useEffect(() => {
    document.title = `${appTheme?.appName} - Class`;
    const bg = async () => {
      const incomingAllSemesterDates = await ConfigDataService.getSemesterDates();
      setAllSemesterDates(incomingAllSemesterDates);
      
      const incomingSchools = await SchoolDataService.getAll();
      setSchools(incomingSchools);

      const incomingTeachers = await UserDataService.getAll();
      const actualTeachers = incomingTeachers.filter(t => t.teacher);
      console.log('Actual teachers', JSON.stringify(actualTeachers, null, 2));
      setTeachers(actualTeachers);
      
      if (id) {
        getClass(id);
        setUpdateMode(true);
        const sfc = await StudentDataService.getAllForClass(id);
        setStudentsForClass(sfc);
        setOriginalStudentIds(new Set(sfc.map(student => student.id)));
        console.log('Students for class', JSON.stringify(sfc, null, 2));
      } else {
        setSelectedSemesterAndYear({ semester: activeSemester, schoolYear: activeYear.year });
        setCurrentClass({ ...currentClass, schoolYear: activeYear.year, semester: activeSemester });
        setLoading(false);
      }
    }
    bg();

    console.log('******** ID', id);
    
  }, [id]);


  useEffect(() => {
    getStudentsForSchool(selectedSchoolId, selectedSemesterAndYear.schoolYear, selectedSemesterAndYear.semester);

    const selectedSchool = schools.find(s => s.id === selectedSchoolId);
    if (selectedSchool) {
      setCurrentClass({ ...currentClass, duration: selectedSchool?.kindergarten ? 25 : 40 });
    }

  }, [selectedSchoolId, selectedSemesterAndYear]);

  useEffect(() => {
    const bg = async () => {
      if (selectedSemesterAndYear.schoolYear) {
        const incomingSemesterDates = await ConfigDataService.getSemesterDatesForYear(selectedSemesterAndYear.schoolYear);
        const incomingVacationDates = await ConfigDataService.getVacationDatesForYear(selectedSemesterAndYear.schoolYear);
        setSemesterDates(incomingSemesterDates);
        setVacationDates(incomingVacationDates);
      }
    };
    bg();
  }, [selectedSemesterAndYear]);

  const handleInputChange = event => {
    const { name, value } = event.target;
    setCurrentClass({ ...currentClass, [name]: value });
  };

  const handleSchoolSelection = event => {
    const { value } = event.target;
    const selectedSchool = schools.find(s => s.id === value);
    setCurrentClass({ ...currentClass, school: selectedSchool });

    
    setSelectedSchoolId(selectedSchool.id);
    setStudentsForClass([]);

  };

  const handleDowSelection = event => {
    const { value } = event.target;
    setCurrentClass({ ...currentClass, day: value });
  };

  const handleYearSelection = event => {
    const { value } = event.target;
    setStudentsForClass([]);
    setSelectedSemesterAndYear({ schoolYear: value, semester: selectedSemesterAndYear.semester });
    setCurrentClass({ ...currentClass, schoolYear: value });
  };

  const handleSemesterSelection = event => {
    const { value } = event.target;
    setSelectedSemesterAndYear({ schoolYear: selectedSemesterAndYear.schoolYear, semester: value });
    setCurrentClass({ ...currentClass, semester: value });
  };

  const handleDatesSubmit = dates => {
    console.log(dates);
    const newDates = dates.map(d => {
      d['hour'] = 4;
      const newDate = new Date(d)
      return newDate;
    });
    setCurrentClass({ ...currentClass, dates: newDates });
    console.log('new dates', newDates);
  }

  const validate = () => {
    const errorFields = [];

    if (currentClass.time) {
      const regex = new RegExp('^([01][0-9]|2[0-3]):?([0-5][0-9])$');

      if (!regex.test(currentClass.time)) {
        setErrorMessage(`Please specify time in HH:MM format`);
        return false;
      }
    }

    if (!currentClass.teacher) {
      errorFields.push('Teacher');
    }

    if (!currentClass.school) {
      errorFields.push('School');
    }

    if (!currentClass.title) {
      //errorFields.push('Title');
      //Default the title
      // eslint-disable-next-line react-hooks/immutability
      currentClass.title = `${currentClass.school.name} ${currentClass.time} ${currentClass.teacher}`
    }

    if (!currentClass.dates || !currentClass.dates.length) {
      errorFields.push('Class Dates');
    }

    if (errorFields.length) {
      setErrorMessage(`These fields may not be blank: ${errorFields.join(',')}`);
      return false;
    } else {
      setErrorMessage(null);
      return true;
    }
  };

  const duplicateClass = () => {
    //what fields to we want to keep?
    const newClass = ClassDataService.initialiseNewClass();

    newClass.time = currentClass.time;
    newClass.teacher = currentClass.teacher;
    newClass.duration = currentClass.duration;
    newClass.dates = [...currentClass.dates];
    newClass.school = currentClass.school;
    newClass.schoolYear = currentClass.schoolYear;
    newClass.classYears = `*${currentClass.classYears}`;
    newClass.semester = currentClass.semester;
    newClass.day = currentClass.day;
    //Not location
    //newClass.location = currentClass.location;

    setUpdateMode(false);
    setStudentsForClass([]);
    setCurrentClass(newClass);
  }

  const moveClassOn = () => {
    //Use this same class content but move it to the 'next' semester

    const newClass = ClassDataService.initialiseNewClass();

    newClass.time = currentClass.time;
    newClass.teacher = currentClass.teacher;
    newClass.duration = currentClass.duration;
    newClass.title = currentClass.title;
    newClass.school = currentClass.school;
    newClass.day = currentClass.day;
    newClass.location = currentClass.location;

    if (currentClass.semester === 'Winter') {
      newClass.classYears = currentClass.classYears;
      newClass.schoolYear = currentClass.schoolYear;
      newClass.semester = 'Summer';
    } else {

      const selectedSemesterDate = allSemesterDates.find(sd => sd.year === currentClass.schoolYear);
      const index = allSemesterDates.indexOf(selectedSemesterDate);
      newClass.schoolYear = allSemesterDates[index + 1]?.year;
      newClass.semester = 'Winter';
    }
    setUpdateMode(false);
    setCurrentClass(newClass);
    setSelectedSemesterAndYear({ schoolYear: newClass.schoolYear, semester: newClass.semester });
  }

  const autoPopulateDates = () => {
    const dates = [];
    //auto pop dates based on day of week, semester start, vacations
    const startDate = moment(semesterDates[selectedSemesterAndYear.semester === 'Winter' ? 'winterDate' : 'summerDate']);
    //set the hour safely out of the way of summertime changes!
    startDate.set({ hour: '5' });
    //move currentDate to next day of week
    let currentDate = nextDayOfWeek(startDate, daysOfWeek.indexOf(currentClass.day));
    console.log('First teching day is:', currentDate);

    while (dates.length < 14) {
      const cd = currentDate;
      //see if currentDate is in a vacation.  If so, spin on
      const isVacationDate = vacationDates.find(vd => {
        const vacationStartDate = moment(vd.startDate);
        const vacationEndDate = moment(vd.endDate);
        console.log('Comparing vacation dates ', vacationStartDate, vacationEndDate, ' to ', cd);
        if (cd.isSameOrAfter(vacationStartDate, 'day') && cd.isSameOrBefore(vacationEndDate, 'day')) {
          return true;
        } else {
          return false;
        }
      })

      if (!isVacationDate) {
        dates.push(currentDate.toDate());
      } else {
        console.log(`${currentDate} is a vacation date`);
      }
      currentDate = currentDate.add(1, 'W');
    }
    console.log('dates', dates);

    setCurrentClass({ ...currentClass, dates });
  };

  const nextDayOfWeek = (dateMoment, dayIndex) => {
    const today = dateMoment.isoWeekday();

    // if we haven't yet passed the day of the week that I need:
    if (today <= dayIndex) {
      // then just give me this week's instance of that day
      return moment(dateMoment).isoWeekday(dayIndex);
    } else {
      // otherwise, give me *next week's* instance of that same day
      return moment(dateMoment).add(1, 'weeks').isoWeekday(dayIndex);
    }
  }
  const addClass = (e) => {
    e.preventDefault();
    
    setSaving(true);
    if (validate()) {
      ClassDataService.create(currentClass, studentsForClass)
        .then(response => {
          console.log(response);
          setMessage("The Class was saved successfully!");
          history.goBack();
        })
        .catch(e => {
          console.log(e);
          setSaving(false);
        });
    } else {
      setSaving(false);
    }
  };

  const updateClass = (e) => {
    e.preventDefault();
    setSaving(true);
    if (validate()) {
      ClassDataService.update(currentClass.id, currentClass, studentsForClass)
        .then(response => {
          console.log(response);
          setMessage("The Class was updated successfully!");
          history.goBack();
        })
        .catch(e => {
          console.log(e);
          setSaving(false);
        });
    } else {
      setSaving(false);
    }
  };

  const deleteClass = (e) => {
    e.preventDefault();
    confirmAlert({
      title: 'Delete Class',
      message: `Are you sure you want to delete class ${currentClass.title ? currentClass.title : ''}?`,
      buttons: [
        {
          label: 'Yes',
          onClick: () => {
            setSaving(true);
            ClassDataService.remove(currentClass.id)
              .then(response => {
                console.log(response);
                history.goBack();
              })
              .catch(e => {
                console.log(e);
                setSaving(false);
              });
          }
        },
        {
          label: 'No',
          onClick: () => { }
        },
      ]
    });
  };

  const DateInput = ({ openCalendar, stringDates, handleValueChange }) => {
    stringDates.sort();
    return (
      <div className="form-control"
        style={{ minHeight: '64px', cursor: 'pointer' }}
        onClick={openCalendar}
      >
        {stringDates.length
          ?
          <ol style={{ paddingLeft: '20px', marginBottom: '0' }}>
            {stringDates.map((d, index) => <li key={index}>{d}</li>)}
          </ol>
          : <div style={{ color: '#6c757d' }}>Click to select dates, or generate them below.</div>
        }
      </div>
    );
  };

  const renderSplitButton = () => {
    return (
      <DropdownButton id="dropdown-basic-button" title="Copy as...">
        <Dropdown.Item onClick={duplicateClass}>New Class</Dropdown.Item>
        <Dropdown.Item onClick={moveClassOn}>Next Semester</Dropdown.Item>
      </DropdownButton>
    )
  }

  const truncate = (input, to) => input.length > to ? `${input.substring(0, to)}...` : input;

  const renderEnrolmentNotes = (notes) => {
    if (!notes) return null;

    // Keep the notes preview on one line and make the info action independent
    // of the parent student row's click-to-move behaviour.
    if (notes.length <= 15) {
      return (
        <span style={{ fontSize: 12, fontStyle: 'italic', color: '#6c757d' }}>
          {`(${notes})`}
        </span>
      );
    }

    return (
      <div
        style={{
          minWidth: 0,
          fontSize: 12,
          fontStyle: 'italic',
          color: '#6c757d',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
        }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {`(${truncate(notes, 45)})`}
        </span>
        <button
          type="button"
          aria-label="Show full student notes"
          title="Show full student notes"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setSelectedNotes(notes);
            setShowNotesModal(true);
          }}
          style={{
            position: 'absolute',
            zIndex: 2,
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 44,
            minWidth: 44,
            height: 44,
            minHeight: 44,
            padding: 0,
            margin: 0,
            border: 'none',
            borderRadius: '4px',
            background: 'transparent',
            color: '#0d6efd',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
          }}
        >
          <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: 36, width: 36, height: 36 }} />
        </button>
      </div>
    );
  }

  const renderBody = () => {
    console.log('rendering body for class', currentClass);
    return (
      <>
        <form>
          {/* FIX: Using responsive grid (row/col) for layout. g-4 provides spacing. */}
          <div className="row g-4">
            <div className="col-12">
              <label htmlFor="title" className="form-label">Title</label>
              <input
                type="text"
                className="form-control"
                id="title"
                placeholder="<schoolName> <time> <teacher>"
                name="title"
                value={currentClass.title}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-12">
              <label htmlFor="notes" className="form-label">Notes</label>
              <textarea
                style={{ height: 100 }}
                className="form-control"
                id="notes"
                name="notes"
                value={currentClass.notes || ''}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-6">
              <label htmlFor="schoolYear" className="form-label">School Year</label>
              <select
                className="form-select"
                id="schoolYear"
                name="schoolYear"
                value={currentClass.schoolYear}
                onChange={handleYearSelection}>
                <option value="">- Select Year -</option>
                {allSemesterDates.map((sd) => <option key={sd.year} value={sd.year}>{sd.year}</option>)}
              </select>
            </div>

            <div className="col-md-6">
              <label htmlFor="semester" className="form-label">Semester</label>
              <select
                className="form-select"
                id="semester"
                name="semester"
                value={currentClass.semester}
                onChange={handleSemesterSelection}>
                <option value="">- Select Semester -</option>
                {semesters.map((semester) => <option key={semester} value={semester}>{semester}</option>)}
              </select>
            </div>

            <div className="col-md-6">
              <label htmlFor="day" className="form-label">Day of Week</label>
              <select
                className="form-select"
                id="day"
                name="day"
                value={currentClass.day}
                onChange={handleDowSelection}>
                <option value="">- Select Day -</option>
                {daysOfWeek.map((dow) => <option key={dow} value={dow}>{dow}</option>)}
              </select>
            </div>

            <div className="col-md-6">
              <label htmlFor="time" className="form-label">Time (HH:mm)</label>
              <input
                type="text"
                className="form-control"
                id="time"
                name="time"
                value={currentClass.time}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-12">
              <label htmlFor="dates" className="form-label">Class Dates</label>
              {/* FIX: Using flexbox for better alignment of date picker and button */}
              <div className="d-flex gap-2">
                <DatePicker
                  render={<DateInput />}
                  multiple
                  sort
                  containerStyle={{ flexGrow: 1 }}
                  onChange={handleDatesSubmit}
                  value={currentClass.dates || []}
                  plugins={[<DatePanel header={currentClass.dates ? `${currentClass.dates.length} Dates` : 'Dates'} />]}
                />
                <button
                  disabled={!selectedSemesterAndYear.schoolYear || !selectedSemesterAndYear.semester || !currentClass.day}
                  className="btn btn-outline-secondary d-flex flex-column justify-content-center align-items-center"
                  type="button"
                  style={{ minHeight: '64px', width: '80px', minWidth: '80px' }}
                  onClick={(e) => { e.stopPropagation(); autoPopulateDates(); }}
                  title="Generate class dates automatically"
                >
                  <i className="fas fa-plus-circle" style={{ fontSize: '1.25rem', marginBottom: '4px' }}></i>
                  <span style={{ fontSize: '0.85rem', fontWeight: '600', lineHeight: '1', textAlign: 'center' }}>Generate</span>
                </button>
              </div>
            </div>

            <div className="col-md-6">
              <label htmlFor="location" className="form-label">Location</label>
              <input
                type="text"
                className="form-control"
                id="location"
                name="location"
                value={currentClass.location}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-6">
              <label htmlFor="duration" className="form-label">Duration (mins)</label>
              <input
                type="number"
                className="form-control"
                // FIX: Corrected duplicate ID. Was "time", now "duration".
                id="duration"
                name="duration"
                value={currentClass.duration}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-6">
              <label htmlFor="teacher" className="form-label">Teacher</label>
              <select
                className="form-select"
                id="teacher"
                name="teacher"
                value={currentClass.teacher}
                onChange={handleInputChange}>
                <option value="">- Select Teacher -</option>
                {teachers.map((teacher) => <option key={teacher.displayName} value={teacher.displayName}>{teacher.displayName}</option>)}
              </select>

            </div>

            <div className="col-md-6">
              <label htmlFor="classYears" className="form-label">Class Year(s)</label>
              <input
                type="text"
                className="form-control"
                id="classYears"
                name="classYears"
                value={currentClass.classYears}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-12">
              <label htmlFor="school" className="form-label">School</label>
              <select
                className="form-select"
                id="school"
                name="school"
                value={currentClass.school.id}
                onChange={handleSchoolSelection}>
                <option value="">- Select School -</option>
                {schools.map((school) => <option key={school.id} value={school.id}>{school.name}</option>)}
              </select>
            </div>

            {/* FIX: Responsive student lists. On large screens they are side-by-side, on smaller they stack. */}
            <div className="col-lg-6">
              <label htmlFor="students-all" className="form-label">All Available Students</label>
              <ul id="students-all" style={studentListStyles}>
                {studentsForSchool.length ?
                  studentsForSchool.map((ss) => {
                    if (studentsForClass.find(sc => sc.id === ss.id)) {
                      return null;
                    } else {
                      return (
                        <li key={ss.id} style={getStudentItemStyles(ss)}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = hasStudentMembershipChanged(ss) ? '#eef8f0' : 'transparent'}
                          onClick={() => addStudentToClass(ss)}>
                          <div style={{ minWidth: 0 }}>
                            <div>{ss.firstName} {ss.lastName} {ss.age && currentClass?.school?.kindergarten ? `(${ss.age})` : ''} {ss.year ? ` - ${ss.year}` : ''} {ss.className}</div>
                            {renderEnrolmentNotes(ss.enrolmentNotes)}
                          </div>
                        </li>);
                    }
                  })
                  : <li style={{ ...studentItemStyles, cursor: 'default', color: '#6c757d' }}>No Students Available</li>
                }
              </ul>
            </div>

            <div className="col-lg-6">
              <label htmlFor="students-selected" className="form-label">{`Selected Students (${studentsForClass.length})`}</label>
              <ul id="students-selected" style={studentListStyles}>
                {studentsForClass.length ?
                  studentsForClass.map((s) => (
                    <li key={s.id} style={getStudentItemStyles(s)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = hasStudentMembershipChanged(s) ? '#eef8f0' : 'transparent'}
                      onClick={() => removeStudentFromClass(s)}>
                      <div style={{ minWidth: 0 }}>
                        <div>{s.firstName} {s.lastName} {s.age && currentClass?.school?.kindergarten ? `(${s.age})` : ''} {s.year ? ` - ${s.year}` : ''} {s.className}</div>
                        {renderEnrolmentNotes(s.parentNotes)}
                      </div>
                    </li>
                  ))
                  : <li style={{ ...studentItemStyles, cursor: 'default', color: '#6c757d' }}>No Students Selected</li>
                }
              </ul>
            </div>
          </div>

          <hr className="my-4" />

          {/* FIX: Responsive button row with modern spacing */}
          {updateMode ?
            <div className="d-flex flex-wrap gap-3 align-items-center">
              <button
                type="submit"
                disabled={saving}
                className="btn btn-primary"
                onClick={updateClass}
              >
                {saving ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
              {renderSplitButton()}
              <button
                className="btn btn-danger ms-auto"
                disabled={saving}
                onClick={deleteClass}
              >
                {saving ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
            :
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary"
              onClick={addClass}
            >
              {saving ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : (
                "Save Class"
              )}
            </button>
          }
        </form>

        <p className="mt-3">{message}</p>
        <p className="errorText">{errorMessage}</p>
      </>
    )
  }

  return (
    <Container className="my-4">
      {/* MODERNIZED: Page is wrapped in a main Card for a modern look */}
      <Card className="shadow-sm">
        <Card.Body className="p-4">
          <h3 className="mb-4">{updateMode ? "Update Class" : "Add Class"}</h3>
          {loading ? <div className='loadingSpinner'><LoadingSpinner /></div> : renderBody()}
        </Card.Body>
      </Card>

      <Modal
        show={showNotesModal}
        onHide={() => setShowNotesModal(false)}
        centered
        scrollable
        aria-labelledby="student-notes-modal-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="student-notes-modal-title">Student notes</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {selectedNotes}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNotesModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Class;